import {
  collectCoreObservability,
} from './coreObservability'

export type LAIAMode =
  | 'READ_ONLY'
  | 'SUPERVISED_FULL'

export interface LAIAHistoryMessage {
  role:
    | 'USER'
    | 'LAIA'
    | 'SYSTEM'

  text: string

  moduleId?: string | null
}

export interface LAIAModuleContext {
  currentModule: string
  route?: string | null
  moduleId?: string | null
  metadata?: Record<string, unknown>
}

export interface LAIAConversationRequest {
  conversationSessionId: string
  message: string
  context: LAIAModuleContext
  history?: LAIAHistoryMessage[]
  mode?: LAIAMode
  requestedBy?: string | null
}

/*
 * Contract observed from the real SEI CORE.
 *
 * Known response fields:
 * contractVersion
 * conversationSessionId
 * status
 * message
 * resolvedIntent
 * moduleContext
 * safety
 * data
 * evidenceIds
 */
export interface LAIAConversationResponse {
  contractVersion?: string
  conversationSessionId?: string
  status?: string
  message: string

  resolvedIntent?: {
    intent?: string
    confidence?: number
    requiresTarget?: boolean
    requiresHumanAuthorization?: boolean
    reason?: string
    candidateCapabilityIds?: string[]
    [key: string]: unknown
  }

  moduleContext?: Record<string, unknown>

  safety?: {
    readOnly?: boolean
    productionChanged?: boolean
    authorizationRequired?: boolean
    authorizationGranted?: boolean
    executionAllowed?: boolean
    [key: string]: unknown
  }

  data?: Record<string, unknown>

  evidenceIds?: string[]

  [key: string]: unknown
}

function asRecord(
  value: unknown,
): Record<string, unknown> | null {
  if (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value)
  ) {
    return value as Record<string, unknown>
  }

  return null
}

function normalizeResponse(
  value: unknown,
): LAIAConversationResponse {
  const record =
    asRecord(value)

  if (!record) {
    throw new Error(
      'LAIA devolvió una respuesta no estructurada.',
    )
  }

  const message =
    typeof record.message === 'string'
      ? record.message.trim()
      : ''

  if (!message) {
    throw new Error(
      'LAIA respondió sin el campo canónico "message".',
    )
  }

  return {
    ...record,
    message,
  } as LAIAConversationResponse
}

export async function sendLAIAConversation(
  request: LAIAConversationRequest,
): Promise<LAIAConversationResponse> {
  const controller =
    new AbortController()

  const timer =
    window.setTimeout(
      () =>
        controller.abort(),
      180_000,
    )


  /*
   * ==========================================================
   * SEI_GENESIS_GUARANTEED_CORE_OBSERVABILITY_TRANSPORT_V1
   *
   * Every real LAIA conversation receives a best-effort
   * READ_ONLY snapshot of the live SEI CORE.
   *
   * If the caller already supplied a valid snapshot, it is
   * preserved.
   *
   * This transport grants:
   * - no authorization
   * - no execution
   * - no Registry write
   * ==========================================================
   */

  const existingMetadata =
    request.context.metadata ?? {}

  let coreObservability =
    existingMetadata.coreObservability

  if (!coreObservability) {
    try {
      coreObservability =
        await collectCoreObservability()
    } catch {
      /*
       * Observability failure must not break
       * ordinary conversation.
       */
      coreObservability =
        undefined
    }
  }

  const enrichedRequest:
    LAIAConversationRequest = {
      ...request,

      context: {
        ...request.context,

        metadata: {
          ...existingMetadata,

          ...(coreObservability
            ? {
                coreObservability,
              }
            : {}),

          observabilityTransport:
            'LAIA_CONVERSATION_SERVICE_V1',

          observabilityTransportAt:
            new Date().toISOString(),
        },
      },
    }

  window.dispatchEvent(
    new CustomEvent(
      'sei:laia-observability-transport',
      {
        detail: {
          attached:
            Boolean(coreObservability),

          source:
            coreObservability &&
            typeof coreObservability === 'object' &&
            'source' in coreObservability
              ? String(
                  (
                    coreObservability as {
                      source?: unknown
                    }
                  ).source ?? ''
                )
              : null,

          successfulSurfaces:
            coreObservability &&
            typeof coreObservability === 'object' &&
            'successfulSurfaces' in coreObservability
              ? Number(
                  (
                    coreObservability as {
                      successfulSurfaces?: unknown
                    }
                  ).successfulSurfaces ?? 0
                )
              : null,
        },
      },
    ),
  )

  try {
    const response =
      await fetch(
        '/api/v1/laia/conversation',
        {
          method:
            'POST',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json',
          },

          credentials:
            'same-origin',

          signal:
            controller.signal,

          body:
            JSON.stringify(
              enrichedRequest,
            ),
        },
      )

    const raw =
      await response.text()

    let decoded: unknown

    try {
      decoded =
        raw
          ? JSON.parse(raw)
          : {}
    } catch {
      throw new Error(
        `LAIA devolvió contenido no JSON: ${raw.slice(0, 500)}`,
      )
    }

    if (!response.ok) {
      throw new Error(
        `LAIA HTTP ${response.status}: ${raw.slice(0, 1000)}`,
      )
    }

    return normalizeResponse(
      decoded,
    )
  } finally {
    window.clearTimeout(
      timer,
    )
  }
}
